import React, { useCallback, useEffect, useState } from "react";

import { Box } from "@/components/layout/flexbox";

type MicrophoneStatus = "granted" | "denied" | "pending";

const CHECK_INTERVAL = 2000; // Check every 2 seconds

export function MicrophoneCheck(): React.ReactElement {
  const [microphoneStatus, setMicrophoneStatus] =
    useState<MicrophoneStatus>("pending");

  const checkMicrophonePermission = useCallback(async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicrophoneStatus("granted");
    } catch (error) {
      if (
        error instanceof DOMException &&
        (error.name === "NotAllowedError" ||
          error.name === "PermissionDeniedError")
      ) {
        setMicrophoneStatus("denied");
      } else {
        setMicrophoneStatus("pending");
      }
    }
  }, []);

  useEffect(() => {
    checkMicrophonePermission();

    const intervalId = setInterval(() => {
      checkMicrophonePermission();
    }, CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkMicrophonePermission]);

  // Don't show anything if permission is granted
  if (microphoneStatus === "granted") return <></>;

  const getCalloutMessage = (): string => {
    switch (microphoneStatus) {
      case "denied":
        return "Microphone access is blocked. Please enable it in your browser settings to continue.";
      case "pending":
      default:
        return "Please allow microphone access when prompted to begin the session.";
    }
  };

  return (
    <Box
      className={`text-xs ${microphoneStatus === "denied" ? "text-red-700" : "text-blue-700"}`}
    >
      {getCalloutMessage()}
    </Box>
  );
}
